import { useEffect, useRef } from "react";

// Open dialogs, innermost last: only the innermost one reacts to Escape and Tab (a manager opened over a drawer closes first).
const OPEN = [];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keyboard behaviour every dialog and drawer needs: focus moves in when it opens, Tab stays inside, Escape closes, and focus
 * goes back to what opened it. Returns the ref to put on the dialog's container. */
export default function useDialogFocus(open, onClose) {
  const ref = useRef(null);
  const opener = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const me = {};
    OPEN.push(me);
    opener.current = document.activeElement;
    const node = ref.current;
    const items = () => (node ? [...node.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement) : []);
    const first = items()[0];
    (first || node)?.focus?.();
    const onKey = (e) => {
      if (OPEN[OPEN.length - 1] !== me) return;
      if (e.key === "Escape") { e.stopPropagation(); close.current?.(); return; }
      if (e.key !== "Tab" || !node) return;
      const list = items();
      if (!list.length) { e.preventDefault(); return; }
      const a = list[0];
      const z = list[list.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      OPEN.splice(OPEN.indexOf(me), 1);
      try { opener.current?.focus?.(); } catch { /* the opener may be gone */ }
    };
  }, [open]);

  return ref;
}
